import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  ScanBarcode, 
  Search, 
  Loader2, 
  CheckCircle2, 
  XCircle,
  Package
} from 'lucide-react';

var API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * BarcodeScanner Component
 * 
 * Supports USB barcode scanners (keyboard emulation mode).
 * USB scanners type the barcode quickly followed by Enter key.
 * 
 * Props:
 * - onProductFound: function(product) - Called when a product is found
 * - onError: function(error) - Called when barcode lookup fails
 * - placeholder: string - Input placeholder text
 * - autoFocus: boolean - Auto-focus the input
 * - showProductPreview: boolean - Show product info after scan
 * - disabled: boolean - Disable the scanner
 * - className: string - Additional CSS classes
 */
function BarcodeScanner(props) {
  var onProductFound = props.onProductFound;
  var onError = props.onError;
  var placeholder = props.placeholder || 'Scan barcode or type SKU...';
  var autoFocus = props.autoFocus !== false;
  var showProductPreview = props.showProductPreview !== false;
  var disabled = props.disabled || false;
  var className = props.className || '';

  var barcodeState = React.useState('');
  var barcode = barcodeState[0];
  var setBarcode = barcodeState[1];

  var loadingState = React.useState(false);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var lastProductState = React.useState(null);
  var lastProduct = lastProductState[0];
  var setLastProduct = lastProductState[1];

  var statusState = React.useState('idle'); // idle, scanning, found, error
  var status = statusState[0];
  var setStatus = statusState[1];

  var inputRef = React.useRef(null);
  var lastKeyTimeRef = React.useRef(0);
  var keyBufferRef = React.useRef('');

  // USB scanner detection: if keys come in rapidly (< 50ms apart), it's a scanner
  var SCANNER_THRESHOLD_MS = 50;

  function handleKeyDown(e) {
    var now = Date.now();
    var timeSinceLastKey = now - lastKeyTimeRef.current;
    
    // If Enter is pressed
    if (e.key === 'Enter') {
      e.preventDefault();
      
      // If we have a barcode, look it up
      if (barcode.trim()) {
        lookupBarcode(barcode.trim());
      }
      return;
    }
    
    // Track rapid input (scanner detection)
    if (timeSinceLastKey < SCANNER_THRESHOLD_MS && timeSinceLastKey > 0) {
      // This is likely a scanner - show scanning indicator
      setStatus('scanning');
    }
    
    lastKeyTimeRef.current = now;
  }

  function lookupBarcode(code) {
    if (!code || loading) return;
    
    setLoading(true);
    setStatus('scanning');
    var token = localStorage.getItem('token');
    
    axios.get(API + '/products/barcode/' + encodeURIComponent(code), {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(response) {
      var product = response.data;
      setLastProduct(product);
      setStatus('found');
      setLoading(false);
      setBarcode(''); // Clear for next scan
      
      // Play success beep (optional)
      playBeep(true);
      
      // Callback with found product
      if (onProductFound) {
        onProductFound(product);
      }
      
      toast.success('Product found: ' + product.name);
      
      // Refocus input for next scan
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }).catch(function(error) {
      setStatus('error');
      setLoading(false);
      setLastProduct(null);
      
      // Play error beep
      playBeep(false);
      
      var errorMsg = error.response?.data?.detail || 'Product not found';
      toast.error(errorMsg);
      
      if (onError) {
        onError(errorMsg);
      }
      
      // Keep barcode in input for manual correction
      if (inputRef.current) {
        inputRef.current.select();
      }
    });
  }

  function playBeep(success) {
    try {
      var audioContext = new (window.AudioContext || window.webkitAudioContext)();
      var oscillator = audioContext.createOscillator();
      var gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = success ? 800 : 300; // Higher pitch for success
      oscillator.type = 'sine';
      gainNode.gain.value = 0.1;
      
      oscillator.start();
      oscillator.stop(audioContext.currentTime + (success ? 0.1 : 0.3));
    } catch (e) {
      // Audio not supported, silently ignore
    }
  }

  function handleManualSearch() {
    if (barcode.trim()) {
      lookupBarcode(barcode.trim());
    }
  }

  function clearLastProduct() {
    setLastProduct(null);
    setStatus('idle');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }

  function getStatusIcon() {
    if (loading) {
      return React.createElement(Loader2, { className: 'w-4 h-4 animate-spin text-blue-500' });
    }
    if (status === 'found') {
      return React.createElement(CheckCircle2, { className: 'w-4 h-4 text-green-500' });
    }
    if (status === 'error') {
      return React.createElement(XCircle, { className: 'w-4 h-4 text-red-500' });
    }
    if (status === 'scanning') {
      return React.createElement(ScanBarcode, { className: 'w-4 h-4 text-blue-500 animate-pulse' });
    }
    return React.createElement(ScanBarcode, { className: 'w-4 h-4 text-slate-400' });
  }

  return React.createElement('div', { className: 'space-y-2 ' + className },
    // Scanner Input Row
    React.createElement('div', { className: 'flex gap-2' },
      React.createElement('div', { className: 'relative flex-1' },
        React.createElement('div', { className: 'absolute left-3 top-1/2 transform -translate-y-1/2' },
          getStatusIcon()
        ),
        React.createElement(Input, {
          ref: inputRef,
          type: 'text',
          value: barcode,
          onChange: function(e) { setBarcode(e.target.value); setStatus('idle'); },
          onKeyDown: handleKeyDown,
          placeholder: placeholder,
          disabled: disabled || loading,
          autoFocus: autoFocus,
          className: 'pl-10 font-mono ' + (status === 'error' ? 'border-red-300 focus:ring-red-500' : status === 'found' ? 'border-green-300 focus:ring-green-500' : ''),
          'data-testid': 'barcode-scanner-input'
        })
      ),
      React.createElement(Button, {
        type: 'button',
        onClick: handleManualSearch,
        disabled: disabled || loading || !barcode.trim(),
        variant: 'outline',
        'data-testid': 'barcode-search-btn'
      },
        loading ? React.createElement(Loader2, { className: 'w-4 h-4 animate-spin' }) : React.createElement(Search, { className: 'w-4 h-4' })
      )
    ),

    // Instructions
    React.createElement('p', { className: 'text-xs text-slate-500' },
      React.createElement(ScanBarcode, { className: 'w-3 h-3 inline mr-1' }),
      'Scan barcode with USB scanner or type manually and press Enter'
    ),

    // Product Preview (if found and showProductPreview is true)
    showProductPreview && lastProduct && React.createElement('div', { 
      className: 'p-3 bg-green-50 border border-green-200 rounded-lg',
      'data-testid': 'scanned-product-preview'
    },
      React.createElement('div', { className: 'flex items-start justify-between' },
        React.createElement('div', { className: 'flex items-center gap-3' },
          React.createElement(Package, { className: 'w-8 h-8 text-green-600' }),
          React.createElement('div', null,
            React.createElement('p', { className: 'font-semibold text-green-800' }, lastProduct.name),
            React.createElement('p', { className: 'text-sm text-green-600' }, 
              lastProduct.brand, ' • ', lastProduct.category
            ),
            React.createElement('div', { className: 'flex gap-2 mt-1' },
              React.createElement(Badge, { variant: 'outline', className: 'text-xs' }, 
                'SKU: ', lastProduct.product_id
              ),
              React.createElement(Badge, { variant: 'outline', className: 'text-xs' }, 
                'Stock: ', lastProduct.current_stock, ' ', lastProduct.measurement_unit
              ),
              lastProduct.barcode && React.createElement(Badge, { variant: 'outline', className: 'text-xs font-mono' }, 
                lastProduct.barcode
              )
            )
          )
        ),
        React.createElement(Button, {
          variant: 'ghost',
          size: 'sm',
          onClick: clearLastProduct,
          className: 'text-green-600 hover:text-green-800'
        }, 'Clear')
      )
    )
  );
}

export default BarcodeScanner;
